import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import useMode from '../../hooks/useMode';
import { JobWithHazardSurvey } from './HazardSurveyThirdPane';
import { KeyValLink } from '@/components/Accordion/KeyValVariants';
import { Flex } from 'rebass';
import IconLink from '@/components/Icon/IconLink';
import useNotify from '@/hooks/useNotify';
import {
  HazardResultRow,
  makeHazardResultColumns,
} from './hazardSurveyResultColumns';
import { GridTable } from '@/components/Table/GridTable';

type HazardSurveyDetailProps = {
  job: JobWithHazardSurvey;
  onChange?: () => void;
};

const HazardSurveyDetail: React.FC<HazardSurveyDetailProps> = ({ job }) => {
  const mode = useMode();
  const items = job.waterHazardSurvey?.items || [];
  const contact = job.waterHazardSurvey?.contact;
  const { showSuccess } = useNotify();

  // const outboundStatus = job.outboundAssignment?.status;
  const surveyStatus = job.waterHazardSurvey?.status;
  const isComplete =
    surveyStatus === 'IN_REVIEW' || surveyStatus === 'ACCEPTED';

  const survey = job.waterHazardSurvey;
  const resultComments: string | undefined = survey?.comments ?? undefined;

  const existingHazardIds = new Set(
    (survey?.waterService?.waterServiceHazards ?? []).map(
      (h: any) => h.waterHazardId
    )
  );

  const isAccepted = surveyStatus === 'ACCEPTED';

  // Show present hazards always; show NO/absent hazards only if they were
  // previously on the water service (display with strikethrough), unless accepted
  const allResultItems = items
    .filter(
      (item) =>
        (item.answer && item.answer !== 'NO') ||
        (!isAccepted && existingHazardIds.has(item.waterHazardId))
    )
    .slice()
    .sort((a, b) => {
      // present items first, then removed/NO items
      const aPresent = a.answer && a.answer !== 'NO' ? 0 : 1;
      const bPresent = b.answer && b.answer !== 'NO' ? 0 : 1;
      return aPresent - bPresent;
    });

  return (
    <>
      {(job.outboundAssignment?.vendorProfile ||
        (job.secureKey && !isComplete)) && (
        <Accordion title="Survey">
          {job.secureKey && !isComplete && (
            <AccordionItem
              label="Secure Link"
              value={
                <Flex flexDirection="row" sx={{ gap: 2 }}>
                  <IconLink
                    icon="copy"
                    iconSize={3}
                    onClick={() => {
                      const value = `${window.location.protocol}//${window.location.host}/hazard-survey/${job.secureKey}`;
                      navigator.clipboard.writeText(value);
                      showSuccess(`Copied "${value}" to clipboard`, 3000);
                    }}
                    iconAtEnd
                    isPrimary
                    p="0"
                  >
                    copy
                  </IconLink>
                  <KeyValLink
                    target="_blank"
                    href={`${window.location.protocol}//${window.location.host}/hazard-survey/${job.secureKey}`}
                  >
                    open
                  </KeyValLink>
                </Flex>
              }
            />
          )}
          {job.outboundAssignment?.vendorProfile && (
            <AccordionItem
              label="Assigned To"
              kind="link"
              value={job.outboundAssignment.vendorProfile.name}
              href={`/${mode}/testers/${job.outboundAssignment.vendorProfile.id}`}
            />
          )}
        </Accordion>
      )}

      <Accordion title="Water Service">
        <AccordionItem
          label="Service #"
          kind={job.waterHazardSurvey?.waterService.id ? 'link' : 'text'}
          value={job.waterHazardSurvey?.waterService.siteNumber || '-'}
          href={
            job.waterHazardSurvey?.waterService.id
              ? `/${mode}/water-services/${job.waterHazardSurvey.waterService.id}`
              : undefined
          }
        />
        {job.waterHazardSurvey?.waterService.meterNumber && (
          <AccordionItem
            label="Meter #"
            value={job.waterHazardSurvey.waterService.meterNumber}
          />
        )}
        {job.waterHazardSurvey?.waterService.hazardSite?.name && (
          <AccordionItem
            label="Site Name"
            value={job.waterHazardSurvey.waterService.hazardSite.name}
          />
        )}
        {job.waterHazardSurvey?.waterService.hazardSite?.siteNumber && (
          <AccordionItem
            label="Site #"
            value={job.waterHazardSurvey.waterService.hazardSite.siteNumber}
          />
        )}
        <AccordionItem
          label="Address"
          value={<Address address={job.address} />}
        />
        <AccordionItem
          label="Backflow Assemblies"
          value={String(
            job.waterHazardSurvey?.waterService?.backflowAssemblies?.length ?? 0
          )}
        />
      </Accordion>
      {job.scheduled && (
        <Accordion title="Visit">
          <AccordionItem
            label="Scheduled"
            value={new Date(job.scheduled).toLocaleString()}
          />
        </Accordion>
      )}
      {contact && (
        <Accordion title="Site Contact">
          <AccordionItem
            label="Name"
            kind={contact.id ? 'link' : 'text'}
            value={contact.name}
            href={contact.id ? `/${mode}/customers/${contact.id}` : undefined}
          />
          {contact.phones.length > 0 && (
            <AccordionItem label="Phone" value={contact.phones[0].value} />
          )}
          {contact.emails.length > 0 && (
            <AccordionItem label="Email" value={contact.emails[0].value} />
          )}
        </Accordion>
      )}
      {isComplete && (
        <>
          <Accordion title="Results">
            {allResultItems.length > 0 ? (
              <GridTable
                columns={makeHazardResultColumns(
                  survey?.waterService?.backflowAssemblies ?? []
                )}
                items={allResultItems.map(
                  (item): HazardResultRow => ({
                    id: item.waterHazardId,
                    name: item.waterHazard.name,
                    hazardLevel: item.waterHazard.hazardLevel as 'LOW' | 'HIGH',
                    answer: item.answer as HazardResultRow['answer'],
                    isProtected:
                      item.isProtected as HazardResultRow['isProtected'],
                    backflowAssemblyId: item.backflowAssemblyId ?? null,
                    removed: !item.answer || item.answer === 'NO',
                  })
                )}
              />
            ) : (
              survey?.otherHazardsAnswer === 'NO' && 'No hazards found.'
            )}

            {survey?.otherHazardsQuestion &&
              survey?.otherHazardsAnswer !== 'NO' && (
                <AccordionItem
                  label="Other Hazards"
                  kind="chip"
                  status={
                    survey?.otherHazardsAnswer === 'UNKNOWN'
                      ? 'warning'
                      : 'error'
                  }
                  value={survey?.otherHazardsAnswer}
                />
              )}
            {resultComments && (
              <AccordionItem label="Comments" value={resultComments} />
            )}
          </Accordion>
          <Accordion title="Submitted By">
            <AccordionItem label="Name" value={survey?.submitterName} />
            <AccordionItem label="Email" value={survey?.submitterEmail} />
            <AccordionItem label="Phone" value={survey?.submitterPhone} />
          </Accordion>
        </>
      )}
    </>
  );
};

HazardSurveyDetail.propTypes = {
  job: PropTypes.any,
};

export default HazardSurveyDetail;
